import React, { useEffect } from 'react';
import { useSelector } from 'react-redux';

import PageLoader from 'components/PageLoader';
import Button from 'components/Button';

import useDispatchWithUnwrap from 'hooks/useDispatchWithUnwrap';
import { getStudentLoanData } from 'selectors/getStudentLoanData';
import { getStudentLoanAssistanceData, getApplicationData as getApplicationDataThunk } from 'thunks';
import { getApplicationData } from 'selectors/getApplicationData';

import styles from './LoaderWrapper.module.scss';

type StudentLoanLoaderWrapperProps = {
  children?: JSX.Element;
};

const StudentLoanLoaderWrapper = ({ children }: StudentLoanLoaderWrapperProps) => {
  const dispatchWithUnwrap = useDispatchWithUnwrap();
  const studentLoanData = useSelector(getStudentLoanData);
  const { application, isLoading: isLoadingApplication } = useSelector(getApplicationData);
  const { applicationId, isLoading, fetched, refetch, error } = studentLoanData;

  useEffect(() => {
    if (applicationId && !fetched && !isLoading && !error) {
      dispatchWithUnwrap(getStudentLoanAssistanceData({ applicationId })).catch(() => undefined);
    }
  }, [applicationId, fetched, isLoading, error, dispatchWithUnwrap]);

  useEffect(() => {
    if (applicationId && !application && !isLoadingApplication) {
      dispatchWithUnwrap(getApplicationDataThunk(applicationId)).catch(() => undefined);
    }
  }, [applicationId, application, isLoadingApplication, dispatchWithUnwrap]);

  const retry = () => {
    if (applicationId) {
      dispatchWithUnwrap(getStudentLoanAssistanceData({ applicationId })).catch(() => undefined);
    }
  };

  if (isLoading && !refetch) {
    return <PageLoader />;
  }

  if (error && !fetched && !refetch) {
    return (
      <div className={styles.container}>
        <div className={styles.title}>We couldn&apos;t load your student loan estimate.</div>
        <p className={styles.description}>Please try again.</p>
        <Button onClick={retry}>Try again</Button>
      </div>
    );
  }

  if ((isLoading || !fetched) && !refetch) {
    return <PageLoader />;
  }

  return <>{children}</>;
};

export default StudentLoanLoaderWrapper;
